import { useState, useEffect, useContext, useMemo } from "react"
import { useNavigate } from "react-router-dom"
import dashboardService from "../../services/DashboardService.js"
import RevenueChart from "./RevenueChart.js"
import {
  buildDateRange,
  formatCurrency,
  getGraphTitle,
  isCustomRangeIncomplete,
  mapRevenueGraph,
} from "./dashboardUtils.js"
import { AuthContext } from "../../context/AuthContext.js"
import { Package, AlertTriangle, Banknote, Wallet, LayoutDashboard, ChevronDown } from "lucide-react"
import { PageLayout, Button, StatusBadge } from "../common/index.js"

const AgentDashboard = () => {
  const navigate = useNavigate()
  const { user } = useContext(AuthContext)
  const [statsLoading, setStatsLoading] = useState(true)
  const [chartLoading, setChartLoading] = useState(true)
  const [tableLoading, setTableLoading] = useState(true)

  const [globalDateFilter, setGlobalDateFilter] = useState("current")
  const [customStartDate, setCustomStartDate] = useState("")
  const [customEndDate, setCustomEndDate] = useState("")

  const [shipmentStats, setShipmentStats] = useState({ total: 0, filtered: 0 })
  const [revenueStats, setRevenueStats] = useState({ total: 0, filtered: 0, paid: 0 })
  const [dueStats, setDueStats] = useState({ total: 0, filtered: 0 })
  const [commissionStats, setCommissionStats] = useState({ total: 0, pending: 0, paid: 0, records: 0 })

  const [openMenus, setOpenMenus] = useState({})

  const [recentShipments, setRecentShipments] = useState([])
  const [recentPayments, setRecentPayments] = useState([])
  const [revenueData, setRevenueData] = useState([])
  const [graphGranularity, setGraphGranularity] = useState("monthly")
  const [commissionRecords, setCommissionRecords] = useState([])
  const [commissionLoading, setCommissionLoading] = useState(true)

  const dateRange = useMemo(
    () => buildDateRange({ preset: globalDateFilter, customStartDate, customEndDate }),
    [globalDateFilter, customStartDate, customEndDate]
  )

  const customRangeIncomplete = isCustomRangeIncomplete(globalDateFilter, customStartDate, customEndDate)

  // Overview fetch for KPI cards and revenue chart
  useEffect(() => {
    if (!user?.user_id || customRangeIncomplete) {
      setRevenueData([])
      setChartLoading(false)
      return
    }

    const fetchOverview = async () => {
      try {
        setStatsLoading(true)
        setChartLoading(true)
        const data = await dashboardService.getOverview({ fromDate: dateRange.from, toDate: dateRange.to })

        setShipmentStats({ total: data.shipments.total_count, filtered: data.shipments.total_count })
        setRevenueStats({ total: data.revenue_due.total_revenue, filtered: data.revenue_due.total_revenue, paid: data.shipments.paid_amount })
        setDueStats({ total: data.revenue_due.total_due, filtered: data.revenue_due.total_due })
        setCommissionStats({
          total: data.commission.grand_total.total,
          paid: data.commission.grand_total.paid,
          pending: data.commission.grand_total.pending,
          records: data.commission.total_records,
        })
        setRevenueData(mapRevenueGraph(data.revenue_graph))
        setGraphGranularity(data.graph_granularity || "monthly")
      } catch (error) {
        console.error("Error fetching dashboard overview:", error)
        setRevenueData([])
      } finally {
        setStatsLoading(false)
        setChartLoading(false)
      }
    }
    fetchOverview()
  }, [user, dateRange, customRangeIncomplete])

  // Recent shipments
  useEffect(() => {
    if (!user?.user_id || customRangeIncomplete) return

    const fetchRecentShipments = async () => {
      try {
        setTableLoading(true)
        const response = await dashboardService.getShipmentRecords({
          fromDate: dateRange.from,
          toDate: dateRange.to,
          page: 1,
          perPage: 5,
        })
        setRecentShipments(response.shipments || [])
      } catch (error) {
        console.error("Error fetching recent shipments:", error)
      } finally {
        setTableLoading(false)
      }
    }
    fetchRecentShipments()
  }, [user, dateRange, customRangeIncomplete])

  // Recent payments
  useEffect(() => {
    if (!user?.user_id || customRangeIncomplete) return

    const fetchRecentPayments = async () => {
      try {
        const response = await dashboardService.getPaymentRecords({
          fromDate: dateRange.from,
          toDate: dateRange.to,
          page: 1,
          perPage: 5,
        })
        setRecentPayments(response.payments || [])
      } catch (error) {
        console.error("Error fetching recent payments:", error)
      }
    }
    fetchRecentPayments()
  }, [user, dateRange, customRangeIncomplete])

  // Commission records — fetch all and filter client-side for this agent
  useEffect(() => {
    if (!user?.user_id || customRangeIncomplete) return

    const fetchCommissionRecords = async () => {
      try {
        setCommissionLoading(true)
        const response = await dashboardService.getCommissionRecords({
          fromDate: dateRange.from,
          toDate: dateRange.to,
          perPage: 100,
        })
        const allRecords = response.records || response.commissions || []

        const myRecords = allRecords.reduce((acc, record) => {
          let myAmount = 0
          const extractAgent = (hubs) => {
            (hubs || []).forEach((hub) => {
              (hub.agents || []).forEach((agent) => {
                if (String(agent.agent_id) === String(user.user_id)) {
                  myAmount += parseFloat(agent.agent_commission_amount || 0)
                }
              })
            })
          }
          extractAgent(record.sender_hub_commissions)
          extractAgent(record.receiver_hub_commissions)

          if (myAmount > 0) {
            acc.push({
              commission_id: record.commission_id,
              ml_sheet_id: record.ml_sheet_id,
              company_name: record.company_name || '-',
              owner_name: record.owner_name || '-',
              my_commission_amount: myAmount,
              company_payment_status: record.company_payment_status,
              created_at: record.created_at,
            })
          }
          return acc
        }, [])

        setCommissionRecords(myRecords)
      } catch (error) {
        console.error("Error fetching commission records:", error)
      } finally {
        setCommissionLoading(false)
      }
    }
    fetchCommissionRecords()
  }, [user, dateRange, customRangeIncomplete])


  return (
    <PageLayout
      title={`Welcome, ${user?.name || "Agent"}`}
      icon={LayoutDashboard}
      actions={
        <>
          <div className="relative">
            <Button size="sm" icon={ChevronDown} iconPosition="right" onClick={() => setOpenMenus({ ...openMenus, quickLinks: !openMenus.quickLinks })}>
              Quick Links
            </Button>
            {openMenus.quickLinks && (
              <div className="absolute left-0 mt-2 w-44 bg-white border border-gray-200 rounded-card shadow-card z-20 py-1">
                <button onClick={() => { navigate("/shipments/add"); setOpenMenus({ ...openMenus, quickLinks: false }); }} className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100 text-secondary">Book Shipment</button>
                <button onClick={() => { navigate("/ml-sheets"); setOpenMenus({ ...openMenus, quickLinks: false }); }} className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100 text-secondary">ML Sheet</button>
                <button onClick={() => { navigate("/daily-earnings"); setOpenMenus({ ...openMenus, quickLinks: false }); }} className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100 text-secondary">Daily Earnings</button>
              </div>
            )}
          </div>
          <select
            value={globalDateFilter}
            onChange={(e) => {
              setGlobalDateFilter(e.target.value)
              if (e.target.value !== "custom") { setCustomStartDate(""); setCustomEndDate("") }
            }}
            className="input-clean w-auto text-sm py-1.5"
          >
            <option value="current">This Month</option>
            <option value="last3months">Last 3 Months</option>
            <option value="last6months">Last 6 Months</option>
            <option value="lastyear">Last Year</option>
            <option value="all">All Time</option>
            <option value="custom">Custom</option>
          </select>
          {globalDateFilter === "custom" && (
            <div className="flex gap-2">
              <input type="date" value={customStartDate} onChange={(e) => setCustomStartDate(e.target.value)} className="input-clean text-xs py-1.5" />
              <input type="date" value={customEndDate} onChange={(e) => setCustomEndDate(e.target.value)} className="input-clean text-xs py-1.5" />
            </div>
          )}
        </>
      }
    >

                {/* Stats Grid - 4 Cards for Agent */}
                <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                  {/* Card 1: My Shipments */}
                  <div className="bg-white rounded-card shadow-card p-4 pt-3 hover:shadow-lg transition-shadow relative min-h-[120px] lg:min-h-0">
                    <div className="flex mb-3">
                      <div className="flex-1">
                        <p className="text-secondary text-xs font-medium">My Shipments</p>
                        {statsLoading ? <div className="h-6 w-16 bg-gray-200 animate-pulse rounded mt-1" /> : <p className="text-xl font-bold text-dark mt-1">{shipmentStats.filtered}</p>}
                      </div>
                    </div>
                    <div className="p-2 absolute rounded-md right-2 bottom-2 bg-green-600">
                      <Package className="w-5 h-5 text-white" />
                    </div>
                  </div>

                  {/* Card 2: My Revenue */}
                  <div className="bg-white rounded-card shadow-card p-4 pt-3 hover:shadow-lg transition-shadow relative min-h-[120px] lg:min-h-0">
                    <div className="flex mb-3">
                      <div className="flex-1">
                        <p className="text-secondary text-xs font-medium">Total Amount</p>
                        {statsLoading ? <div className="h-6 w-16 bg-gray-200 animate-pulse rounded mt-1" /> : <p className="text-xl font-bold text-dark mt-1">{formatCurrency(revenueStats.filtered)}</p>}
                        <p className="text-sm text-secondary mt-3 absolute font-semibold bottom-3">Paid: {formatCurrency(revenueStats.paid)}</p>
                      </div>
                      <div className="bg-purple-500 p-2 absolute rounded-md right-2 bottom-2">
                        <Banknote className="w-5 h-5 text-white" />
                      </div>
                    </div>
                  </div>

                  {/* Card 3: Pending Due */}
                  <div className="bg-white rounded-card shadow-card p-4 pt-3 hover:shadow-lg transition-shadow relative min-h-[120px] lg:min-h-0">
                    <div className="flex mb-3">
                      <div className="flex-1">
                        <p className="text-secondary text-xs font-medium">Pending Due</p>
                        {statsLoading ? <div className="h-6 w-16 bg-gray-200 animate-pulse rounded mt-1" /> : <p className="text-xl font-bold text-dark mt-1">{formatCurrency(dueStats.filtered)}</p>}
                      </div>
                      <div className="bg-blue-600 p-2 absolute rounded-md right-2 bottom-2">
                        <AlertTriangle className="w-5 h-5 text-white" />
                      </div>
                    </div>
                  </div>

                  {/* Card 4: My Commission */}
                  <div className="bg-white rounded-card shadow-card p-4 pt-3 hover:shadow-lg transition-shadow relative min-h-[120px] lg:min-h-0">
                    <div className="flex mb-3">
                      <div className="flex-1">
                        <p className="text-secondary text-xs font-medium">My Commission {user?.shipment_commission ? <span className="text-blue-600">({user.shipment_commission}%)</span> : ''}</p>
                        {statsLoading ? <div className="h-6 w-16 bg-gray-200 animate-pulse rounded mt-1" /> : <p className="text-xl font-bold text-dark mt-1">{formatCurrency(commissionStats.pending)}</p>}
                        <p className="text-sm text-secondary mt-3 absolute font-semibold bottom-3">Total: {formatCurrency(commissionStats.total)}</p>
                      </div>
                      <div className="bg-red-500 p-2 absolute rounded-md right-2 bottom-2">
                        <Wallet className="w-5 h-5 text-white" />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Revenue Graph and Recent Shipments */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
                  <div className="lg:col-span-2 bg-white rounded-card shadow-card p-4 md:p-6">
                    <h2 className="text-lg md:text-md font-bold text-tertiary mb-4 font-semibold">
                      {getGraphTitle(graphGranularity).replace("Revenue", "My Revenue")}
                    </h2>
                    {customRangeIncomplete ? (
                      <div className="flex items-center justify-center h-[320px] text-secondary text-sm">
                        Select both start and end dates to view the chart
                      </div>
                    ) : (
                      <RevenueChart data={revenueData} granularity={graphGranularity} loading={chartLoading} />
                    )}
                  </div>

                  {/* Recent Shipments */}
                  <div className="bg-white rounded-card shadow-card p-4 md:p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h2 className="text-md md:text-lg font-semibold text-tertiary">Recent Shipments</h2>
                      <Button variant="ghost" size="sm" onClick={() => navigate("/shipments")}>View All →</Button>
                    </div>
                    <div className="space-y-3">
                      {tableLoading ? (
                        <div className="flex justify-center py-6">
                          <div className="w-6 h-6 border-4 border-brand border-t-transparent rounded-full animate-spin" />
                        </div>
                      ) : recentShipments.length > 0 ? (
                        recentShipments.map((shipment) => (
                          <div
                            key={shipment.shipment_id || shipment.id}
                            className="p-3 bg-light rounded-md hover:bg-gray-100 cursor-pointer transition-colors"
                            onClick={() => navigate(`/shipments/view/${shipment.shipment_id}`)}
                          >
                            <div className="flex justify-between items-start">
                              <div>
                                <p className="font-semibold text-dark text-sm">{shipment.shipment_id}</p>
                                <p className="text-xs text-secondary">{shipment.sender_name}</p>
                              </div>
                              <StatusBadge status={shipment.status} />
                            </div>
                          </div>
                        ))
                      ) : (
                        <p className="text-secondary text-sm text-center py-4">No recent shipments</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Recent Payments */}
                <div className="bg-white rounded-card shadow-card p-4 md:p-6 mb-6">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg md:text-xl font-bold text-dark">My Recent Payments</h2>
                    <Button variant="ghost" size="sm" onClick={() => navigate("/payment-history")}>View All →</Button>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {recentPayments.length > 0 ? (
                      recentPayments.map((payment) => (
                        <div
                          key={payment.id}
                          className="p-4 bg-light rounded-md hover:bg-gray-100 cursor-pointer transition-colors"
                          onClick={() => navigate(`/payment-history/edit/${payment.id}`)}
                        >
                          <div className="flex justify-between items-start mb-2">
                            <div>
                              <p className="font-semibold text-dark">₹{parseFloat(payment.payment_amount || 0).toLocaleString()}</p>
                              <p className="text-xs text-secondary mt-1">Shipment: {payment.shipment_id}</p>
                            </div>
                            <span className="px-2 py-1 bg-green-100 text-green-800 rounded text-xs font-semibold">
                              {payment.payment_mode}
                            </span>
                          </div>
                          <p className="text-xs text-secondary">{payment.payment_date ? new Date(payment.payment_date).toLocaleDateString() : ''}</p>
                        </div>
                      ))
                    ) : (
                      <p className="text-secondary text-sm text-center py-8 col-span-3">No recent payments</p>
                    )}
                  </div>
                </div>

                {/* My Commission Records */}
                <div className="bg-white rounded-card shadow-card p-4 md:p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg md:text-xl font-bold text-dark flex items-center gap-2">
                      <Wallet className="w-5 h-5 text-blue-500" /> My Commission Records
                    </h2>
                    <Button variant="ghost" size="sm" onClick={() => navigate("/commissions")}>View All →</Button>
                  </div>

                  {/* Commission Summary Bar */}
                  {!statsLoading && (
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                      {user?.shipment_commission > 0 && (
                        <div className="bg-blue-50 border border-blue-200 rounded-sm p-3 text-center">
                          <p className="text-xs text-blue-600 mb-1">My Rate</p>
                          <p className="text-lg font-bold text-blue-800">{user.shipment_commission}%</p>
                        </div>
                      )}
                      <div className="bg-brand-light border border-blue-200 rounded-sm p-3 text-center">
                        <p className="text-xs text-brand mb-1">Total</p>
                        <p className="text-lg font-bold text-blue-800">{formatCurrency(commissionStats.total)}</p>
                      </div>
                      <div className="bg-green-50 border border-green-200 rounded-sm p-3 text-center">
                        <p className="text-xs text-green-600 mb-1">Paid</p>
                        <p className="text-lg font-bold text-green-800">{formatCurrency(commissionStats.paid)}</p>
                      </div>
                      <div className="bg-yellow-50 border border-yellow-200 rounded-sm p-3 text-center">
                        <p className="text-xs text-yellow-600 mb-1">Pending</p>
                        <p className="text-lg font-bold text-yellow-800">{formatCurrency(commissionStats.pending)}</p>
                      </div>
                    </div>
                  )}

                  {commissionLoading ? (
                    <div className="flex justify-center py-6">
                      <div className="w-6 h-6 border-4 border-brand border-t-transparent rounded-full animate-spin" />
                    </div>
                  ) : commissionRecords.length > 0 ? (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="bg-gray-50">
                            <th className="px-4 py-2 text-left text-xs font-semibold text-gray-700">Commission ID</th>
                            <th className="px-4 py-2 text-left text-xs font-semibold text-gray-700">ML Sheet</th>
                            <th className="px-4 py-2 text-left text-xs font-semibold text-gray-700">Company</th>
                            <th className="px-4 py-2 text-left text-xs font-semibold text-gray-700">Owner</th>
                            <th className="px-4 py-2 text-right text-xs font-semibold text-gray-700">My Amount</th>
                            <th className="px-4 py-2 text-center text-xs font-semibold text-gray-700">Status</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                          {commissionRecords.map((rec) => (
                            <tr
                              key={rec.commission_id}
                              className="hover:bg-gray-50 cursor-pointer"
                              onClick={() => navigate(`/commissions/view/${rec.commission_id}`)}
                            >
                              <td className="px-4 py-3 text-sm font-semibold text-tertiary">{rec.commission_id}</td>
                              <td className="px-4 py-3 text-sm text-gray-600">{rec.ml_sheet_id}</td>
                              <td className="px-4 py-3 text-sm text-gray-600">{rec.company_name}</td>
                              <td className="px-4 py-3 text-sm text-gray-600">{rec.owner_name}</td>
                              <td className="px-4 py-3 text-sm text-right font-bold text-secondary">₹{parseFloat(rec.my_commission_amount || 0).toLocaleString('en-IN')}</td>
                              <td className="px-4 py-3 text-center">
                                <StatusBadge status={rec.company_payment_status} />
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p className="text-secondary text-sm text-center py-8">No commission records found for this period</p>
                  )}
                </div>
    </PageLayout>
  )
}

export default AgentDashboard;
